/**
 * Generates a PDF for Gold feature results (script, preexam, summary).
 * Fully dynamic heights, high contrast, font size 12, multi-page.
 */
export const exportGoldPDF = async (featureType, topic, age, data) => {
  try {
    const { jsPDF } = await import('jspdf');
    const doc = new jsPDF('p', 'mm', 'a4');
    const W = 210;
    const M = 12;
    const CW = W - 2 * M;
    const MAX_W = 170; 

    // Configuración Tipográfica Legible
    const SIZE_LBL = 10;
    const SIZE_TIT = 14;
    const SIZE_BOD = 12;
    const LH_TIT = SIZE_TIT * 0.45; 
    const LH_BOD = SIZE_BOD * 0.45; 

    const LABELS = { script: 'Guión de Video', preexam: 'Pre-Examen', summary: 'Resumen para Alumno' };
    const ACCENTS = { script: [139, 92, 246], preexam: [59, 130, 246], summary: [16, 185, 129] };
    const [r, g, b] = ACCENTS[featureType] || [96, 165, 250];

    let y = 32;

    const drawHeader = () => {
      doc.setFillColor(3, 7, 18);
      doc.rect(0, 0, W, 24, 'F');
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(16);
      doc.setTextColor(r, g, b);
      doc.text('DinamiClass', M, 14);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(10);
      doc.setTextColor(203, 213, 225);
      const headerLabel = `${LABELS[featureType]}  \u00B7  ${topic}  \u00B7  ${age} años`;
      doc.text(headerLabel, W - M - doc.getTextWidth(headerLabel), 14);
      doc.setDrawColor(30, 41, 59);
      doc.setLineWidth(0.3);
      doc.line(0, 24, W, 24);
    };

    const drawFooter = () => {
      doc.setDrawColor(30, 41, 59);
      doc.setLineWidth(0.3);
      doc.line(M, 283, W - M, 283);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      doc.setTextColor(148, 163, 184);
      // ¡Libre de IA!
      doc.text('DinamiClass  \u00B7  Planificación Educativa  \u00B7  dinamiclass.com', M, 289);
      const dt = new Date().toLocaleDateString('es-AR');
      doc.text(dt, W - M - doc.getTextWidth(dt), 289);
    };

    drawHeader();
    drawFooter();

    const checkPageBreak = (neededHeight) => {
      if (y + neededHeight > 275) {
        doc.addPage();
        drawHeader();
        drawFooter();
        y = 32;
      }
    };

    const wrapText = (text, maxW, fontSize, font = 'normal') => {
      if (!text) return [];
      doc.setFontSize(fontSize);
      doc.setFont('helvetica', font);
      return doc.splitTextToSize(String(text), maxW);
    };

    const drawCard = (label, contentRenderer, calculatedHeight) => {
      checkPageBreak(calculatedHeight);
      doc.setFillColor(15, 23, 42); 
      doc.roundedRect(M, y, CW, calculatedHeight, 2.5, 2.5, 'F');
      doc.setFillColor(r, g, b); 
      doc.roundedRect(M, y, 3.5, calculatedHeight, 1, 1, 'F');

      if (label) {
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(SIZE_LBL);
        doc.setTextColor(r, g, b);
        doc.text(String(label), M + 8, y + 9);
        contentRenderer(M + 8, y + 16);
      } else {
        contentRenderer(M + 8, y + 10);
      }
      y += calculatedHeight + 6;
    };

    if (data && data.titulo) {
      const titleLines = wrapText(data.titulo, CW, 16, 'bold');
      checkPageBreak(titleLines.length * 8 + 10);
      doc.setFontSize(16); doc.setFont('helvetica', 'bold'); doc.setTextColor(15, 23, 42); 
      doc.text(titleLines, M, y + 5);
      y += titleLines.length * 8 + 8;
    }

    if (featureType === 'script' && data) {
      if (data.introduccion) {
        const introGancho = wrapText(data.introduccion.gancho || data.introduccion.desafio_apertura, MAX_W, SIZE_BOD, 'italic');
        const introCont = wrapText(data.introduccion.contenido, MAX_W, SIZE_BOD, 'normal');
        let h = 18;
        h += introGancho.length * LH_BOD + 3;
        h += introCont.length * LH_BOD + 4;
        
        drawCard('INTRODUCCIÓN', (x, startY) => {
          let ty = startY;
          doc.setFontSize(SIZE_BOD); doc.setFont('helvetica', 'italic'); doc.setTextColor(203, 213, 225);
          doc.text(introGancho, x, ty);
          ty += introGancho.length * LH_BOD + 3;

          doc.setFontSize(SIZE_BOD); doc.setFont('helvetica', 'normal'); doc.setTextColor(248, 250, 252);
          doc.text(introCont, x, ty);
        }, h);
      }

      (data.secciones || []).forEach(s => {
        const sTit = wrapText(s.titulo, MAX_W, SIZE_TIT, 'bold');
        const sGui = wrapText(s.guion, MAX_W, SIZE_BOD, 'normal');
        const sVis = s.recurso_visual ? wrapText(`Visual: ${s.recurso_visual}`, MAX_W, 10, 'italic') : [];
        let h = 12;
        h += sTit.length * LH_TIT + 3;
        h += sGui.length * LH_BOD + 3;
        if (s.recurso_visual) h += sVis.length * (10 * 0.45) + 3;
        
        drawCard(null, (x, startY) => {
          let ty = startY;
          doc.setFontSize(SIZE_TIT); doc.setFont('helvetica', 'bold'); doc.setTextColor(255, 255, 255);
          doc.text(sTit, x, ty);
          ty += sTit.length * LH_TIT + 3;

          doc.setFontSize(SIZE_BOD); doc.setFont('helvetica', 'normal'); doc.setTextColor(248, 250, 252);
          doc.text(sGui, x, ty);
          ty += sGui.length * LH_BOD + 3;

          if (s.recurso_visual) {
            doc.setFontSize(10); doc.setFont('helvetica', 'italic'); doc.setTextColor(148, 163, 184);
            doc.text(sVis, x, ty);
          }
        }, h);
      });

      if (data.cierre) {
        const cierreLines = wrapText(data.cierre, MAX_W, SIZE_BOD, 'normal');
        drawCard('CIERRE', (x, startY) => {
          doc.setFontSize(SIZE_BOD); doc.setFont('helvetica', 'normal'); doc.setTextColor(248, 250, 252);
          doc.text(cierreLines, x, startY);
        }, 18 + cierreLines.length * LH_BOD);
      }
    }

    // --- PRE-EXAMEN (GOLD) CORREGIDO AL MILÍMETRO ---
    if (featureType === 'preexam' && data) {
      if (data.instrucciones) {
        const instLines = wrapText(data.instrucciones, CW, SIZE_BOD, 'italic');
        checkPageBreak(instLines.length * LH_BOD + 10);
        doc.setFontSize(SIZE_BOD); doc.setFont('helvetica', 'italic'); doc.setTextColor(71, 85, 105);
        doc.text(instLines, M, y);
        y += instLines.length * LH_BOD + 6;
      }

      (data.preguntas || []).forEach((q, i) => {
        const qTit = wrapText(`${q.numero || i + 1}. ${q.pregunta}`, MAX_W, SIZE_TIT, 'bold');
        let h = 18; // Padding superior
        h += qTit.length * LH_TIT + 4;
        
        const opts = (q.opciones || []).map(opt => {
          const isC = String(opt).startsWith(q.respuesta_correcta);
          const oLines = wrapText(`  ${opt}`, MAX_W - 10, SIZE_BOD, isC ? 'bold' : 'normal');
          const oH = oLines.length * LH_BOD + 2;
          return { lines: oLines, isC, h: oH };
        });
        h += opts.reduce((acc, curr) => acc + curr.h, 0);

        // CORRECCIÓN CLAVE: Chau flecha maldita, hola "Explicación:"
        const expLines = q.explicacion ? wrapText(`Explicación: ${q.explicacion}`, MAX_W - 15, 11, 'italic') : [];
        if (q.explicacion) h += 4 + expLines.length * (11 * 0.45) + 4; // Padding extra abajo

        drawCard(null, (x, startY) => {
          let ty = startY;
          doc.setFontSize(SIZE_TIT); doc.setFont('helvetica', 'bold'); doc.setTextColor(255, 255, 255);
          doc.text(qTit, x, ty);
          ty += qTit.length * LH_TIT + 4;

          opts.forEach(o => {
            doc.setFontSize(SIZE_BOD); doc.setFont('helvetica', o.isC ? 'bold' : 'normal'); 
            doc.setTextColor(...(o.isC ? [52, 211, 153] : [241, 245, 249]));
            doc.text(o.lines, x + 4, ty);
            ty += o.h;
          });

          if (q.explicacion) {
            ty += 4;
            doc.setFontSize(11); doc.setFont('helvetica', 'italic'); doc.setTextColor(203, 213, 225);
            doc.text(expLines, x, ty);
          }
        }, h);
      });
    }

    if (featureType === 'summary' && data) {
      if (data.objetivos?.length) {
        let h = 18;
        const objLines = data.objetivos.map(o => wrapText(`• ${o}`, MAX_W, SIZE_BOD, 'normal'));
        h += objLines.reduce((acc, l) => acc + l.length * LH_BOD + 2, 0);
        
        drawCard('OBJETIVOS', (x, startY) => {
          let ty = startY;
          doc.setFontSize(SIZE_BOD); doc.setFont('helvetica', 'normal'); doc.setTextColor(248, 250, 252);
          objLines.forEach(lines => {
            doc.text(lines, x, ty);
            ty += lines.length * LH_BOD + 2;
          });
        }, h);
      }

      if (data.conceptos_clave?.length) {
        let h = 18;
        const concs = data.conceptos_clave.map(c => {
          const tLines = wrapText(`${c.termino}:`, MAX_W, SIZE_BOD, 'bold');
          const dLines = wrapText(c.definicion, MAX_W, SIZE_BOD, 'normal');
          return { tLines, dLines, h: tLines.length * LH_BOD + dLines.length * LH_BOD + 4 };
        });
        h += concs.reduce((acc, c) => acc + c.h, 0);

        drawCard('CONCEPTOS CLAVE', (x, startY) => {
          let ty = startY;
          concs.forEach(c => {
            doc.setFontSize(SIZE_BOD); doc.setFont('helvetica', 'bold'); doc.setTextColor(52, 211, 153);
            doc.text(c.tLines, x, ty);
            ty += c.tLines.length * LH_BOD + 1;

            doc.setFontSize(SIZE_BOD); doc.setFont('helvetica', 'normal'); doc.setTextColor(248, 250, 252);
            doc.text(c.dLines, x, ty);
            ty += c.dLines.length * LH_BOD + 3;
          });
        }, h);
      }

      if (data.resumen_narrativo) {
        const resLines = wrapText(data.resumen_narrativo, MAX_W, SIZE_BOD, 'normal');
        drawCard('RESUMEN', (x, startY) => {
          doc.setFontSize(SIZE_BOD); doc.setFont('helvetica', 'normal'); doc.setTextColor(248, 250, 252);
          doc.text(resLines, x, startY);
        }, 18 + resLines.length * LH_BOD);
      }

      if (data.para_recordar) {
        const recLines = wrapText(`"${data.para_recordar}"`, MAX_W, SIZE_TIT, 'bold');
        drawCard('PARA RECORDAR', (x, startY) => {
          doc.setFontSize(SIZE_TIT); doc.setFont('helvetica', 'bold'); doc.setTextColor(52, 211, 153);
          doc.text(recLines, x, startY);
        }, 18 + recLines.length * LH_TIT);
      }
    }

    const slug = (topic || 'gold').replace(/[^a-zA-Z0-9\s]/g, '').trim().replace(/\s+/g, '-').substring(0, 25);
    doc.save(`DinamiClass-${LABELS[featureType].replace(/\s+/g, '-')}-${slug}.pdf`);

  } catch (e) {
    console.error("Error generando PDF Gold:", e);
    throw e;
  }
};

/**
 * Generates a professional PDF from DinamiClass result data (Standard features).
 * Pure jsPDF – no html2canvas required. Adaptive heights & multi-page.
 */
export const exportToPDF = async (result) => {
  try {
    const { jsPDF } = await import('jspdf');
    const doc = new jsPDF('p', 'mm', 'a4');
    const W = 210;
    const M = 12;
    const CW = W - 2 * M;
    const MAX_W = 170; 

    const SIZE_LBL = 10;
    const SIZE_TIT = 14;
    const SIZE_BOD = 12;
    const LH_TIT = SIZE_TIT * 0.45; 
    const LH_BOD = SIZE_BOD * 0.45; 

    let y = 32;

    const drawHeader = () => {
      doc.setFillColor(3, 7, 18);
      doc.rect(0, 0, W, 24, 'F');
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(16);
      doc.setTextColor(96, 165, 250);
      doc.text('DinamiClass', M, 14);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(10);
      doc.setTextColor(203, 213, 225);
      const topicLabel = `${result?.topic || 'Clase'}  \u00B7  ${result?.age || 'XX'} años`;
      const tw = doc.getTextWidth(topicLabel);
      doc.text(topicLabel, W - M - tw, 14);
      doc.setDrawColor(30, 41, 59);
      doc.setLineWidth(0.3);
      doc.line(0, 24, W, 24);
    };

    const drawFooter = () => {
      doc.setDrawColor(30, 41, 59);
      doc.setLineWidth(0.3);
      doc.line(M, 283, W - M, 283);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      doc.setTextColor(148, 163, 184);
      doc.text('DinamiClass  \u00B7  Planificación Educativa  \u00B7  dinamiclass.com', M, 289);
      const date = new Date().toLocaleDateString('es-AR');
      doc.text(date, W - M - doc.getTextWidth(date), 289);
    };

    drawHeader();
    drawFooter();

    const checkPageBreak = (neededHeight) => {
      if (y + neededHeight > 275) {
        doc.addPage();
        drawHeader();
        drawFooter();
        y = 32;
      }
    };

    const wrapText = (text, maxW, fontSize, font = 'normal') => {
      if (!text) return [];
      doc.setFontSize(fontSize);
      doc.setFont('helvetica', font);
      return doc.splitTextToSize(String(text), maxW);
    };

    const drawCard = (accentRgb, labelColor, label, contentRenderer, calculatedHeight) => {
      checkPageBreak(calculatedHeight);

      doc.setFillColor(15, 23, 42); 
      doc.roundedRect(M, y, CW, calculatedHeight, 2.5, 2.5, 'F');

      doc.setFillColor(...accentRgb); 
      doc.roundedRect(M, y, 3.5, calculatedHeight, 1, 1, 'F');

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(SIZE_LBL);
      doc.setTextColor(...labelColor);
      doc.text(String(label), M + 8, y + 9);

      contentRenderer(M + 8, y + 16);
      y += calculatedHeight + 6;
    };

    const clasesArray = result?.cards?.clases || (result?.cards ? [result.cards] : []);

    if (clasesArray.length === 0) {
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(12);
      doc.setTextColor(0, 0, 0);
      doc.text("No se encontraron dinámicas para exportar.", M, y + 10);
    } else {
      clasesArray.forEach((clase, index) => {
        
        if (clasesArray.length > 1 || clase.titulo_clase) {
          checkPageBreak(15);
          doc.setFont('helvetica', 'bold');
          doc.setFontSize(11);
          doc.setTextColor(148, 163, 184);
          const title = String(clase.titulo_clase || `SESIÓN ${index + 1}`).toUpperCase();
          const dur = clase.duracion_minutos ? ` (${clase.duracion_minutos} MIN)` : '';
          doc.text(`${title}${dur}`, M, y + 5);
          y += 10;
        }

        // --- DESAFÍO DE APERTURA ---
        const apertura = clase.desafio_apertura || clase.gancho;
        if (apertura) {
          const linesTitle = wrapText(apertura.titulo, MAX_W, SIZE_TIT, 'bold');
          const linesPreg = wrapText(apertura.pregunta_polemica, MAX_W, SIZE_BOD, 'normal');
          const linesInst = apertura.instrucciones ? wrapText('Docente: ' + apertura.instrucciones, MAX_W, SIZE_BOD, 'italic') : [];
          
          let h = 18; 
          h += linesTitle.length * LH_TIT + 3;
          h += linesPreg.length * LH_BOD + 3;
          if (apertura.instrucciones) h += linesInst.length * LH_BOD + 3;
          h += 4; 
          
          drawCard([59, 130, 246], [96, 165, 250], `DESAFÍO DE APERTURA  \u00B7  ${apertura.tiempo || '2 min'}`, (x, startY) => {
            let ty = startY;
            doc.setFontSize(SIZE_TIT); doc.setFont('helvetica', 'bold'); doc.setTextColor(255, 255, 255); 
            doc.text(linesTitle, x, ty);
            ty += linesTitle.length * LH_TIT + 3;

            doc.setFontSize(SIZE_BOD); doc.setFont('helvetica', 'normal'); doc.setTextColor(248, 250, 252); 
            doc.text(linesPreg, x, ty);
            ty += linesPreg.length * LH_BOD + 3;

            if (apertura.instrucciones) {
              doc.setFontSize(SIZE_BOD); doc.setFont('helvetica', 'italic'); doc.setTextColor(203, 213, 225); 
              doc.text(linesInst, x, ty);
            }
          }, h);
        }

        // --- RETO GAMIFICADO ---
        const reto = clase.reto;
        if (reto) {
          const linesTitle = wrapText(reto.titulo, MAX_W, SIZE_TIT, 'bold');
          const linesDesc = wrapText(reto.descripcion, MAX_W, SIZE_BOD, 'normal');
          const linesInst = reto.instrucciones ? wrapText(reto.instrucciones, MAX_W, SIZE_BOD, 'normal') : [];
          
          let h = 18;
          h += linesTitle.length * LH_TIT + 3;
          h += linesDesc.length * LH_BOD + 3;
          if (reto.instrucciones) h += linesInst.length * LH_BOD + 3;
          h += 4;
          
          drawCard([139, 92, 246], [167, 139, 250], `EL RETO GAMIFICADO  \u00B7  ${reto.tiempo || '15 min'}`, (x, startY) => {
            let ty = startY;
            doc.setFontSize(SIZE_TIT); doc.setFont('helvetica', 'bold'); doc.setTextColor(255, 255, 255);
            doc.text(linesTitle, x, ty);
            ty += linesTitle.length * LH_TIT + 3;

            doc.setFontSize(SIZE_BOD); doc.setFont('helvetica', 'normal'); doc.setTextColor(248, 250, 252);
            doc.text(linesDesc, x, ty);
            ty += linesDesc.length * LH_BOD + 3;

            if (reto.instrucciones) {
              doc.setFontSize(SIZE_BOD); doc.setFont('helvetica', 'normal'); doc.setTextColor(203, 213, 225);
              doc.text(linesInst, x, ty);
            }
          }, h);
        }

        // --- MICRO-EVALUACIÓN ---
        const evaluacion = clase.evaluacion;
        if (evaluacion) {
          const linesTitle = wrapText(evaluacion.titulo, MAX_W, SIZE_TIT, 'bold');
          let h = 18;
          h += linesTitle.length * LH_TIT + 4;
          
          const qs = evaluacion.preguntas || [];
          const parsedQs = qs.map((q, i) => {
              const qLines = wrapText(`${i + 1}. ${q.pregunta}`, MAX_W, SIZE_BOD, 'bold');
              let qH = qLines.length * LH_BOD + 2;
              
              const parsedOpts = (q.opciones || []).map(opt => {
                  const isCorrect = String(opt).startsWith(q.respuesta_correcta);
                  const optLines = wrapText(`  ${opt}`, MAX_W - 10, SIZE_BOD, 'normal');
                  const optH = optLines.length * LH_BOD + 1.5;
                  return { lines: optLines, isCorrect, h: optH };
              });
              
              const optsH = parsedOpts.reduce((acc, curr) => acc + curr.h, 0);
              qH += optsH + 4; 
              return { qLines, parsedOpts, totalH: qH };
          });
          
          h += parsedQs.reduce((acc, curr) => acc + curr.totalH, 0);
          h += 4;

          drawCard([16, 185, 129], [52, 211, 153], `MICRO-EVALUACION  \u00B7  5 minutos`, (x, startY) => {
            let ty = startY;
            doc.setFontSize(SIZE_TIT); doc.setFont('helvetica', 'bold'); doc.setTextColor(255, 255, 255);
            doc.text(linesTitle, x, ty);
            ty += linesTitle.length * LH_TIT + 4;

            parsedQs.forEach(pq => {
              doc.setFontSize(SIZE_BOD); doc.setFont('helvetica', 'bold'); doc.setTextColor(248, 250, 252);
              doc.text(pq.qLines, x, ty);
              ty += pq.qLines.length * LH_BOD + 2;

              doc.setFontSize(SIZE_BOD); doc.setFont('helvetica', 'normal');
              pq.parsedOpts.forEach(po => {
                if (po.isCorrect) doc.setTextColor(52, 211, 153); 
                else doc.setTextColor(203, 213, 225); 
                doc.text(po.lines, x + 4, ty); // Ligeramente indentado
                ty += po.h;
              });
              ty += 4;
            });
          }, h);
        }
      });
    }

    const slug = (result?.topic || 'clase')
      .replace(/[áàä]/gi, 'a').replace(/[éèë]/gi, 'e')
      .replace(/[íìï]/gi, 'i').replace(/[óòö]/gi, 'o')
      .replace(/[úùü]/gi, 'u').replace(/[^a-zA-Z0-9\s]/g, '')
      .trim().replace(/\s+/g, '-').substring(0, 30);
    doc.save(`DinamiClass-${slug}.pdf`);

  } catch (e) {
    console.error("Error generando PDF:", e);
    throw e;
  }
};

// ============================================================================
// NUEVO: EXPORTACIÓN DE SECUENCIA MAESTRA (ULTRA)
// ============================================================================
export const exportMSToPDF = async (msData, subjectName = 'General') => {
  try {
    const { jsPDF } = await import('jspdf');
    const doc = new jsPDF('p', 'mm', 'a4');
    const W = 210;
    const M = 12;
    const CW = W - 2 * M;
    const MAX_W = 170; 

    const SIZE_LBL = 10;
    const SIZE_TIT = 14;
    const SIZE_BOD = 12;
    const LH_TIT = SIZE_TIT * 0.45; 
    const LH_BOD = SIZE_BOD * 0.45; 

    // Color dorado/ámbar para la Secuencia Maestra
    const r = 245, g = 158, b = 11;

    let y = 32;

    const drawHeader = () => {
      doc.setFillColor(3, 7, 18);
      doc.rect(0, 0, W, 24, 'F');
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(16);
      doc.setTextColor(r, g, b);
      doc.text('DinamiClass Ultra', M, 14);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(10);
      doc.setTextColor(203, 213, 225);
      const headerLabel = `Secuencia Maestra  \u00B7  ${subjectName}`;
      doc.text(headerLabel, W - M - doc.getTextWidth(headerLabel), 14);
      doc.setDrawColor(30, 41, 59);
      doc.setLineWidth(0.3);
      doc.line(0, 24, W, 24);
    };

    const drawFooter = () => {
      doc.setDrawColor(30, 41, 59);
      doc.setLineWidth(0.3);
      doc.line(M, 283, W - M, 283);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      doc.setTextColor(148, 163, 184);
      doc.text('DinamiClass Ultra  \u00B7  Planificación Educativa', M, 289);
      const dt = new Date().toLocaleDateString('es-AR');
      doc.text(dt, W - M - doc.getTextWidth(dt), 289);
    };

    drawHeader();
    drawFooter();

    const checkPageBreak = (neededHeight) => {
      if (y + neededHeight > 275) {
        doc.addPage();
        drawHeader();
        drawFooter();
        y = 32;
      }
    };

    const wrapText = (text, maxW, fontSize, font = 'normal') => {
      if (!text) return [];
      doc.setFontSize(fontSize);
      doc.setFont('helvetica', font);
      return doc.splitTextToSize(String(text), maxW);
    };

    const drawCard = (label, contentRenderer, calculatedHeight) => {
      checkPageBreak(calculatedHeight);
      doc.setFillColor(15, 23, 42); 
      doc.roundedRect(M, y, CW, calculatedHeight, 2.5, 2.5, 'F');
      doc.setFillColor(r, g, b); 
      doc.roundedRect(M, y, 3.5, calculatedHeight, 1, 1, 'F');

      if (label) {
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(SIZE_LBL);
        doc.setTextColor(r, g, b);
        doc.text(String(label), M + 8, y + 9);
        contentRenderer(M + 8, y + 16);
      } else {
        contentRenderer(M + 8, y + 10);
      }
      y += calculatedHeight + 6;
    };

    // Extraer arreglo de semanas ciegamente
    const getSemanasData = () => {
      if (!msData) return [];
      if (Array.isArray(msData)) return msData;
      const foundArray = Object.values(msData).find(val => Array.isArray(val));
      return foundArray || [];
    };

    const semanas = getSemanasData();
    const tituloSecuencia = msData?.titulo || `Secuencia Maestra: ${subjectName}`;

    if (tituloSecuencia) {
      const titleLines = wrapText(tituloSecuencia, CW, 16, 'bold');
      checkPageBreak(titleLines.length * 8 + 10);
      doc.setFontSize(16); doc.setFont('helvetica', 'bold'); doc.setTextColor(15, 23, 42); 
      doc.text(titleLines, M, y + 5);
      y += titleLines.length * 8 + 8;
    }

    if (semanas.length === 0) {
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(12);
      doc.setTextColor(0, 0, 0);
      doc.text("No se encontró estructura de secuencia para exportar.", M, y + 10);
    } else {
      semanas.forEach((sem, idx) => {
        const modulo = String(sem.modulo || sem.semana || `Semana ${idx + 1}`).toUpperCase();
        const temaTitle = wrapText(sem.tema || sem.titulo || 'Tema a definir', MAX_W, SIZE_TIT, 'bold');
        const objLines = wrapText(`Objetivo: ${sem.objetivo || sem.descripcion || 'Sin especificar'}`, MAX_W, SIZE_BOD, 'normal');
        const biblioLines = wrapText(`Bibliografía: ${sem.bibliografia_recomendada || sem.bibliografia || 'No requerida'}`, MAX_W, SIZE_BOD, 'italic');
        
        let h = 16;
        h += temaTitle.length * LH_TIT + 3;
        h += objLines.length * LH_BOD + 3;
        h += biblioLines.length * LH_BOD + 3;

        drawCard(modulo, (x, startY) => {
          let ty = startY;
          doc.setFontSize(SIZE_TIT); doc.setFont('helvetica', 'bold'); doc.setTextColor(255, 255, 255);
          doc.text(temaTitle, x, ty);
          ty += temaTitle.length * LH_TIT + 4;

          doc.setFontSize(SIZE_BOD); doc.setFont('helvetica', 'normal'); doc.setTextColor(203, 213, 225);
          doc.text(objLines, x, ty);
          ty += objLines.length * LH_BOD + 4;

          doc.setFontSize(SIZE_BOD); doc.setFont('helvetica', 'italic'); doc.setTextColor(245, 158, 11);
          doc.text(biblioLines, x, ty);
        }, h);
      });
    }

    const slug = subjectName.replace(/[^a-zA-Z0-9\s]/g, '').trim().replace(/\s+/g, '-').substring(0, 25) || 'Secuencia';
    doc.save(`DinamiClass-Secuencia-${slug}.pdf`);

  } catch (e) {
    console.error("Error generando PDF de Secuencia Maestra:", e);
    throw e;
  }
};
